import { useEffect, useRef } from "react";
import * as THREE from "three";
import { heroOrbitIcons } from "@/mocks/serra";

const NODE_COLORS = [0xf89b37, 0x45b6da, 0xf76b45, 0x69b25a];

interface LogoPixel {
  x: number;
  y: number;
  r: number;
  g: number;
  b: number;
}

function sampleLogoPixels(url: string, maxCount: number): Promise<LogoPixel[]> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const size = 140;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("no 2d context"));
        return;
      }
      ctx.drawImage(img, 0, 0, size, size);
      const { data } = ctx.getImageData(0, 0, size, size);
      const candidates: LogoPixel[] = [];
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const i = (y * size + x) * 4;
          if (data[i + 3] > 80) {
            candidates.push({
              x: (x / size) * 2 - 1,
              y: -((y / size) * 2 - 1),
              r: data[i] / 255,
              g: data[i + 1] / 255,
              b: data[i + 2] / 255,
            });
          }
        }
      }
      for (let i = candidates.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
      }
      resolve(candidates.slice(0, maxCount));
    };
    img.onerror = () => reject(new Error("failed to load logo"));
    img.src = url;
  });
}

export default function HeroScene() {
  const mountRef = useRef<HTMLDivElement>(null);
  const labelHostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;
    const labelHost = labelHostRef.current;
    if (!mount || !labelHost) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
    camera.position.set(0, 0, 6.2);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    const rig = new THREE.Group();
    scene.add(rig);

    // core — a dense pulsing particle nucleus shaped like the Serra logo
    const core = new THREE.Group();
    rig.add(core);
    let logoPoints: THREE.Points | null = null;
    let cancelled = false;

    sampleLogoPixels("/logo.png", 1500)
      .then((pixels) => {
        if (cancelled || pixels.length === 0) return;
        const scale = 0.95;
        const positions = new Float32Array(pixels.length * 3);
        const colors = new Float32Array(pixels.length * 3);
        pixels.forEach((p, i) => {
          positions[i * 3] = p.x * scale;
          positions[i * 3 + 1] = p.y * scale;
          positions[i * 3 + 2] = (Math.random() - 0.5) * 0.15;
          colors[i * 3] = p.r;
          colors[i * 3 + 1] = p.g;
          colors[i * 3 + 2] = p.b;
        });
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
        logoPoints = new THREE.Points(
          geometry,
          new THREE.PointsMaterial({
            size: 0.032,
            vertexColors: true,
            transparent: true,
            opacity: 0.95,
            sizeAttenuation: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );
        core.add(logoPoints);
      })
      .catch(() => {
        // fallback: soft generic cluster if the logo can't be sampled
        const count = 260;
        const positions = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
          const r = Math.pow(Math.random(), 1.6) * 0.5;
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.acos(Math.random() * 2 - 1);
          positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
          positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
          positions[i * 3 + 2] = r * Math.cos(phi);
        }
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        logoPoints = new THREE.Points(
          geometry,
          new THREE.PointsMaterial({
            color: 0xf89b37,
            size: 0.04,
            transparent: true,
            opacity: 0.8,
            sizeAttenuation: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );
        core.add(logoPoints);
      });

    // orbit rings
    const ringConfigs = [
      { radius: 1.7, tilt: 0.5, color: 0x45b6da, speed: 0.06 },
      { radius: 2.15, tilt: -0.7, color: 0x213b5c, speed: -0.08 },
      { radius: 2.55, tilt: 1.1, color: 0x67666a, speed: 0.05 },
    ];
    const rings = ringConfigs.map((c) => {
      const group = new THREE.Group();
      group.rotation.x = c.tilt;
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(c.radius, 0.004, 8, 96),
        new THREE.MeshBasicMaterial({
          color: c.color,
          transparent: true,
          opacity: 0.35,
        })
      );
      group.add(ring);
      rig.add(group);
      return { group, speed: c.speed };
    });

    // dust particles
    const dustCount = 160;
    const dustPositions = new Float32Array(dustCount * 3);
    for (let i = 0; i < dustCount; i++) {
      const r = 1.6 + Math.random() * 1.8;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      dustPositions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      dustPositions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      dustPositions[i * 3 + 2] = r * Math.cos(phi);
    }
    const dustGeometry = new THREE.BufferGeometry();
    dustGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(dustPositions, 3)
    );
    const dust = new THREE.Points(
      dustGeometry,
      new THREE.PointsMaterial({
        color: 0xffffff,
        size: 0.02,
        transparent: true,
        opacity: 0.35,
        sizeAttenuation: true,
      })
    );
    rig.add(dust);

    // orbiting icon nodes: line + HTML label overlay
    const radius = 2.0;
    const speed = 0.18;
    const nodes = heroOrbitIcons.map((node, i) => {
      const angleOffset = (i / heroOrbitIcons.length) * Math.PI * 2;
      const color = NODE_COLORS[i % NODE_COLORS.length];

      const lineGeometry = new THREE.BufferGeometry();
      const linePositions = new Float32Array([0, 0, 0, radius, 0, 0]);
      lineGeometry.setAttribute(
        "position",
        new THREE.BufferAttribute(linePositions, 3)
      );
      const line = new THREE.Line(
        lineGeometry,
        new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.4 })
      );
      rig.add(line);

      const el = document.createElement("div");
      el.style.position = "absolute";
      el.style.transform = "translate(-50%, -50%)";
      el.style.pointerEvents = "none";
      el.style.display = "flex";
      el.style.flexDirection = "column";
      el.style.alignItems = "center";
      el.style.gap = "6px";
      const hex = `#${color.toString(16).padStart(6, "0")}`;
      el.innerHTML = `
        <span style="width:44px;height:44px;display:flex;align-items:center;justify-content:center;border-radius:12px;background:rgba(8,22,36,0.9);border:1.5px solid ${hex};color:${hex};box-shadow:0 0 18px -3px ${hex};font-size:18px;">
          <i class="${node.icon}"></i>
        </span>
        <span style="font-size:11px;color:rgba(255,255,255,0.8);white-space:nowrap;">${node.label}</span>
      `;
      labelHost.appendChild(el);

      return { angleOffset, radius, speed, line: lineGeometry, el };
    });

    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    const light1 = new THREE.PointLight(0x45b6da, 30, 18);
    light1.position.set(3, 2, 4);
    const light2 = new THREE.PointLight(0xf89b37, 20, 16);
    light2.position.set(-3, -2, 3);
    scene.add(ambient, light1, light2);

    const pointer = { x: 0, y: 0, targetX: 0, targetY: 0 };
    function onPointerMove(e: PointerEvent) {
      const rect = mount!.getBoundingClientRect();
      pointer.targetX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.targetY = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    }
    window.addEventListener("pointermove", onPointerMove);

    function project(vec: THREE.Vector3) {
      const v = vec.clone().project(camera);
      return {
        x: (v.x * 0.5 + 0.5) * width,
        y: (-v.y * 0.5 + 0.5) * height,
      };
    }

    let frameId = 0;
    const startTime = performance.now();
    let lastTime = startTime;

    function animate() {
      frameId = requestAnimationFrame(animate);
      const now = performance.now();
      const t = (now - startTime) / 1000;
      const delta = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      core.scale.setScalar(1 + Math.sin(t * 1.2) * 0.06);
      if (logoPoints) {
        logoPoints.rotation.y = Math.sin(t * 0.15) * 0.3;
      }

      rings.forEach(({ group, speed: s }) => {
        group.rotation.z += delta * s;
      });

      dust.rotation.y += delta * 0.02;

      pointer.x += (pointer.targetX - pointer.x) * 0.04;
      pointer.y += (pointer.targetY - pointer.y) * 0.04;
      rig.rotation.y += (pointer.x * 0.35 - rig.rotation.y) * 0.06;
      rig.rotation.x += (-pointer.y * 0.22 - rig.rotation.x) * 0.06;

      nodes.forEach(({ angleOffset, radius: r, speed: sp, line, el }) => {
        const a = t * sp + angleOffset;
        const x = Math.cos(a) * r;
        const z = Math.sin(a) * r;
        const y = Math.sin(a * 0.7) * 0.35;
        const posAttr = line.attributes.position as THREE.BufferAttribute;
        posAttr.setXYZ(1, x, y, z);
        posAttr.needsUpdate = true;

        const worldPos = new THREE.Vector3(x, y, z).applyMatrix4(rig.matrixWorld);
        const screen = project(worldPos);
        el.style.left = `${screen.x}px`;
        el.style.top = `${screen.y}px`;
      });

      rig.updateMatrixWorld();
      renderer.render(scene, camera);
    }

    if (reduceMotion) {
      renderer.render(scene, camera);
    } else {
      animate();
    }

    function onResize() {
      if (!mount) return;
      width = mount.clientWidth;
      height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    }
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onPointerMove);
      nodes.forEach(({ el }) => el.remove());
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div ref={mountRef} className="absolute inset-0">
      <div ref={labelHostRef} className="absolute inset-0 pointer-events-none overflow-hidden" />
    </div>
  );
}
